import { useAdminProducts } from '@hooks/useAdminProducts'
import useFormatPrice from '@hooks/useFormatPrice'
import SearchBar from '@components/SearchBar'
import { Navigate } from 'react-router-dom'
import Loading from '@components/Loading'
import useAuth from '@hooks/useAuth'
import PropTypes from 'prop-types'
import { useState } from 'react'
import styles from '@styles/CrudProduct.module.css'
import ProductForm from '@components/ProductForm'
import ProductTable from '@components/ProductTable'

const AdminProducts = ({ darkMode = false }) => {
  const { products, handleCreateProduct, handleUpdateProduct, handleDeleteProduct, handleToggleStatus } =
    useAdminProducts()
  const { user, loading: authLoading } = useAuth()
  const formatPrice = useFormatPrice()
  const [searchTerm, setSearchTerm] = useState('')
  const [searchFilter, setSearchFilter] = useState('id')
  const [currentPage, setCurrentPage] = useState(1)
  const [limit, setLimit] = useState(5)
  const [editingProduct, setEditingProduct] = useState(null)

  if (authLoading) return <Loading />
  if (!user) return <Navigate to='/login' />

  const sortedProducts = products.sort((a, b) => a.id - b.id)

  const handleNextPage = () => currentPage * limit < filteredProducts.length && setCurrentPage(currentPage + 1)
  const handlePreviousPage = () => currentPage > 1 && setCurrentPage(currentPage - 1)

  const handleLimitChange = (e) => {
    setLimit(Number(e.target.value))
    setCurrentPage(1)
  }

  const filterFunctions = {
    id: (product) => product.id.toString().includes(searchTerm),
    category: (product) => product.category.toLowerCase().includes(searchTerm.toLowerCase()),
    brand: (product) => product.brand.toLowerCase().includes(searchTerm.toLowerCase()),
    reference: (product) => product.reference.toLowerCase().includes(searchTerm.toLowerCase()),
    price: (product) => formatPrice(product.price).includes(searchTerm),
  }

  const filteredProducts = sortedProducts.filter(product => {
    if (filterFunctions[searchFilter]) return filterFunctions[searchFilter](product, searchTerm)
    return true
  })

  const paginatedProducts = filteredProducts.slice((currentPage - 1) * limit, currentPage * limit)

  const handleSubmit = async product => {
    if (editingProduct) {
      await handleUpdateProduct(editingProduct.id, product)
      setEditingProduct(null)
    } else {
      await handleCreateProduct(product)
    }
  }

  const toggleStatus = async (id, currentStatus) => {
    await handleToggleStatus(id, currentStatus)
  }

  return (
    <div className={`${styles.container} ${darkMode ? styles.dark : ''}`}>
      <h1 className={styles.title}>Gestionar productos</h1>

      <ProductForm
        onSubmit={handleSubmit}
        darkMode={darkMode}
      />

      <SearchBar
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        searchFilter={searchFilter}
        setSearchFilter={setSearchFilter}
        className={darkMode ? styles.darkSearch : ''}
      />

      <ProductTable
        products={paginatedProducts}
        onDelete={handleDeleteProduct}
        onEdit={setEditingProduct}
        onToggleStatus={toggleStatus}
        formatPrice={formatPrice}
        darkMode={darkMode}
      />

      {editingProduct && (
        <div className={styles.overlay}>
          <div className={styles.editCard}>
            <button
              onClick={() => setEditingProduct(null)}
              className={styles.closeButton}
            >
              x
            </button>
            <ProductForm
              product={editingProduct}
              onSubmit={handleSubmit}
              darkMode={darkMode}
              isEditing={true}
            />
          </div>
        </div>
      )}

      <div className={styles.limitSelector}>
        <label htmlFor='limit'>Mostrar productos por página: </label>
        <select
          id='limit'
          name='limit'
          value={limit}
          onChange={handleLimitChange}
        >
          <option value='5'>5</option>
          <option value='10'>10</option>
          <option value='30'>30</option>
          <option value='50'>50</option>
          <option value='100'>100</option>
        </select>
      </div>
      <div className={styles.pagination}>
        <button onClick={handlePreviousPage} disabled={currentPage === 1}>
          Anterior
        </button>
        <button
          onClick={handleNextPage}
          disabled={currentPage * limit >= filteredProducts.length}
        >
          Siguiente
        </button>
      </div>
    </div>
  )
}

AdminProducts.propTypes = {
  darkMode: PropTypes.bool,
}

export default AdminProducts

